import React, { FunctionComponent, useMemo, useEffect } from "react";
import { useRouter } from "next/router";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@/models/appState/RootState";
import Script from "next/script";
import { GetServerSideProps } from "next";
import HeadDetails from "@/components/responsive/layout/head";
import LoadingProgressiveResource from "@/components/responsive/layout/LoadingProgressiveResource";
import ResponsiveLayout from "@/components/responsive/ResponsiveLayout";
import Scripts from "@/components/Default/Layout/Head/Scripts";
import WrapperContainer from "@/components/WrapperContainer";
import { ProductPage } from "@/components/responsive/product/ProductPage";
import { Product as productModel } from "@/models/scope/productSheet/Product";
import { MenuItems } from "@/models/common/header/MenuItems";
import { getDefaultMenu } from "@/redux/reducers/responsive/common/CommonActions";
import { setAxiosServerConfig } from "@/services/ApiCaller/apiCallerSSR";
import { getCustomMenu } from "@/services/menuServices";
import { getProductDetails } from "@/services/productServices";
import Utils from "@/libs/Utils";
import { DEFAULT_TEMPLATE_VERSION, MOBILE_TEMPLATE_VERSION } from "@/utils/constants";
import { getBuildId } from "@/utils/communUtil";
import { PRODUCT_SHEET_PAGE } from "@/utils/constants";
import Cookies from "cookie";
import {isEligibleToScan} from "@/utils/Mobile/utils";

interface Props {
    templateVersion: string;
    menuItems: MenuItems;
    aboveMenuItems: MenuItems;
    productData: productModel;
    userAgent: string;
}

const Product: FunctionComponent<Props> = ({
    templateVersion,
    menuItems: initialMenuItems,
    aboveMenuItems,
    productData,
    userAgent
}) => {
    const router = useRouter();
    const pathName = router.pathname;

    const stateMenuItems = useSelector((state: RootState) => state.menu.menuItems.values);
    const isDefaultMenu = useSelector((state: RootState) => state.menu.isDefaultMenu);
    const menuItems = isDefaultMenu ? stateMenuItems : initialMenuItems;

    const { metaInfos, images, logoVersion, footerTrackingScopeData, headerTrackingScopeData,
        googleMapsKey, isDQENormalisationActive, shopCircleVersion, id, adaptiveLoadingResources, showBanners,
        bannersBlocList
    } = productData;
    const countDownElement = bannersBlocList?.filter(item => {
        return item.hasCountDown === true;
    });
    const hasCountDown = countDownElement?.length ? true : false;
    const dispatch = useDispatch();
    useEffect(() => {
        dispatch(getDefaultMenu(templateVersion, userAgent));

    }, []);
    const memoizedLinks = useMemo(() => {
        const links = [
            <link key="productsheet" rel="stylesheet" href={`/assets/css/responsive/productsheet.css?v=${getBuildId()}`} />,
        ];
        if (templateVersion === DEFAULT_TEMPLATE_VERSION) {
            links.push(
                <link key="default-header-footer" rel="stylesheet" href={`/assets/css/default/headerFooter.css?v=${getBuildId()}`} />
            );
        }
        if (templateVersion === MOBILE_TEMPLATE_VERSION) {
            links.push(
                <link key="mobile-header-footer" rel="stylesheet" href={`/assets/css/mobile/headerFooter.css?v=${getBuildId()}`} />
            );
        }
        return links;
    }, [templateVersion]);
    const memoizedScripts = useMemo(() => {
        const js = [
            <>
                <LoadingProgressiveResource resourceName={'Ressource6'}
                                            resources={adaptiveLoadingResources}>
                    {!isEligibleToScan() && (
                        <Script src={"/assets/js/targetToSell.min.js?v=" + getBuildId()} strategy="afterInteractive"/>)}
                </LoadingProgressiveResource>
                <Script src="/assets/js/imageMapResizer.min.js" strategy="beforeInteractive" />
                <Script src="/assets/js/owl.carousel.min.js" strategy="beforeInteractive" />
                <Script type="text/tc_privacy" data-category="3" src="/assets/js/Default/octipasMerchLibraryConfig.js" strategy="afterInteractive" />
            </>
        ];
        if (templateVersion === MOBILE_TEMPLATE_VERSION) {
            js.push(
                <Script src="/assets/js/slick.min.js" strategy="beforeInteractive"/>
            );
        }
        return js;
    }, [adaptiveLoadingResources])

    return (
        <WrapperContainer context={PRODUCT_SHEET_PAGE} templateVersion={templateVersion}>
            <HeadDetails
                title={Utils.getMetaInfosValueByCode("METATitle", metaInfos)}
                description={Utils.getMetaInfosValueByCode("METADescription", metaInfos)}
                keywords={Utils.getMetaInfosValueByCode("METAKeywords", metaInfos)}
                canonical={Utils.getMetaInfosValueByCode("canonicalUrl", metaInfos)}
                context={PRODUCT_SHEET_PAGE}
                images={images}
            >
                {memoizedLinks}
            </HeadDetails>
            <Scripts>
                {memoizedScripts}
            </Scripts>
            <ResponsiveLayout
                context={PRODUCT_SHEET_PAGE}
                pathName={pathName}
                templateVersion={templateVersion}
                menuItems={menuItems}
                aboveMenuItems={aboveMenuItems}
                ssr={true}
                currentStep={PRODUCT_SHEET_PAGE}
                logoVersion={logoVersion}
                footerTrackingScopeData={footerTrackingScopeData}
                headerTrackingScopeData={headerTrackingScopeData}
                googleMapsKey={googleMapsKey}
                isDQENormalisationActive={isDQENormalisationActive}
                shopCircleVersion={shopCircleVersion}
                showBanners={showBanners}
                bannersBlocList={bannersBlocList}
                hasCountDown={hasCountDown}
                productId={id}>
                <ProductPage productData={productData} templateVersion={templateVersion} />
            </ResponsiveLayout>
        </WrapperContainer>
    )
}

export const getServerSideProps: GetServerSideProps = async ({ res, req, query }) => {
    setAxiosServerConfig(res);
    let deviceType = req.headers['cloudfront-is-mobile-viewer'] == 'false' ?
        DEFAULT_TEMPLATE_VERSION :
        MOBILE_TEMPLATE_VERSION;
    const parsedCookies = req.headers.cookie ? Cookies.parse(req.headers.cookie) : null;
    const userAgent = req.headers['user-agent'];
    const cUserID = (parsedCookies && parsedCookies.CUserID) ? parsedCookies.CUserID : '';
    //TODO TS: delete @ts-ignore once the project is fully migrated to type script
    /**@ts-ignore */
    const productResult = await getProductDetails(query.id, 'Full', `/produits/${query.id}/${query.title}.html`, userAgent, cUserID, deviceType, req.url)
    const productData = productResult.data;
    let headers = new Headers(productData.headers);
    if ([301, 302, 307].includes(productResult.status) && headers.get('navigateTo')) {
        res.writeHead(productResult.status, {
            Location: headers.get('navigateTo') as string
        });
        res.end();
    } else if (productResult.status === 404) {
        res.writeHead(302, {
            Location: "/Common/Page/NotFound"
        });
        res.end();
    }
    let menuResponse;
    if (productData.menuIdNav) {
        menuResponse = await getCustomMenu(deviceType, userAgent, productData.menuIdNav.split(";"), false, cUserID, true, req.url);
    } else {
        menuResponse = await getCustomMenu(deviceType, userAgent, null, true, cUserID, true, req.url);
    }

    return {

        props: {
            menuItems: menuResponse.menuItems.values,
            aboveMenuItems: menuResponse?.aboveMenuItems.hasOwnProperty('values') ?
                menuResponse.aboveMenuItems.values :
                [],
            productData,
        }
    }
}

export default Product;
